import React, { useMemo, useState } from 'react';
import { Search, ArrowUp, ArrowDown, Download, SlidersHorizontal } from 'lucide-react';
import { t } from '../i18n';

// Componente base de tabla de datos del ERP. Una sola densidad (filas ~34px),
// sin animaciones de entrada. El prop `variant` se conserva por compatibilidad
// con pantallas existentes pero ya no cambia el estilo.
const DataTable = ({
  data = [],
  columns = [],
  searchPlaceholder = 'Buscar...',
  onRowClick = () => {},
  className = '',
  selectedRowId = null,
  tableMaxHeight = null,
  selectable = false,
  onSelectionChange = null,
  totals = null,
  onExport = null,
  loading = false,
  emptyTitle = 'No se encontraron resultados',
  emptyDescription = 'Intenta con otros términos de búsqueda o ajusta los filtros.'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());
  const [columnWidths, setColumnWidths] = useState({});
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);

  const visibleColumns = useMemo(
    () => columns.filter((column) => !hiddenKeys.has(column.key)),
    [columns, hiddenKeys]
  );

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const filteredData = data.filter((row) => {
    if (!searchTerm) return true;
    return columns.some((column) => {
      const value = row[column.key];
      return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
    });
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortColumn) return 0;
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const allSelected = sortedData.length > 0 && sortedData.every((row) => selectedIds.has(row.id));
  const someSelected = !allSelected && sortedData.some((row) => selectedIds.has(row.id));

  const emitSelection = (next) => {
    setSelectedIds(next);
    if (onSelectionChange) {
      onSelectionChange(data.filter((row) => next.has(row.id)));
    }
  };

  const toggleRow = (rowId, event) => {
    event.stopPropagation();
    const next = new Set(selectedIds);
    if (next.has(rowId)) next.delete(rowId); else next.add(rowId);
    emitSelection(next);
  };

  const toggleAll = () => {
    if (allSelected) {
      emitSelection(new Set());
    } else {
      emitSelection(new Set(sortedData.map((row) => row.id)));
    }
  };

  const startResize = (columnKey, startX, startWidth) => {
    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      setColumnWidths((prev) => ({ ...prev, [columnKey]: Math.max(64, startWidth + delta) }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const exportCsv = () => {
    if (onExport) {
      onExport(sortedData);
      return;
    }
    const header = visibleColumns.map((column) => `"${column.label}"`).join(',');
    const rows = sortedData.map((row) =>
      visibleColumns
        .map((column) => `"${(row[column.key] ?? '').toString().replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`bg-white rounded border border-neutral-200 ${className}`}>
      <div className="p-2.5 border-b border-neutral-100 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
          <input
            type="text"
            placeholder={t(searchPlaceholder)}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-8 pl-8 pr-3 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
          />
        </div>

        <div className="flex items-center gap-2">
          {selectable && selectedIds.size > 0 && (
            <span className="text-xs text-neutral-500">{t(selectedIds.size)} {t('seleccionada(s)')}</span>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={() => setColumnsMenuOpen((open) => !open)}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium border border-neutral-300 rounded text-neutral-600 hover:bg-neutral-50"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />{t('Columnas')}</button>
            {columnsMenuOpen && (
              <div className="absolute right-0 mt-1 w-56 bg-white border border-neutral-200 rounded shadow-lg z-10 p-1.5">
                {columns.map((column) => (
                  <label key={column.key} className="flex items-center gap-2 px-2 py-1 text-sm text-neutral-700 hover:bg-neutral-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!hiddenKeys.has(column.key)}
                      onChange={() => {
                        setHiddenKeys((prev) => {
                          const next = new Set(prev);
                          if (next.has(column.key)) next.delete(column.key); else next.add(column.key);
                          return next;
                        });
                      }}
                    />
                    {t(column.label)}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={exportCsv}
            className="inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium border border-neutral-300 rounded text-neutral-600 hover:bg-neutral-50"
          >
            <Download className="w-3.5 h-3.5" />{t('Exportar')}</button>
        </div>
      </div>

      <div
        className={tableMaxHeight ? 'overflow-auto' : 'overflow-x-auto'}
        style={tableMaxHeight ? { maxHeight: tableMaxHeight } : undefined}
      >
        <table className="w-full border-collapse">
          <thead className="bg-neutral-50 border-b border-neutral-200 sticky top-0">
            <tr>
              {selectable && (
                <th className="w-9 px-3 py-2 border-r border-neutral-100">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAll}
                  />
                </th>
              )}
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  style={columnWidths[column.key] ? { width: columnWidths[column.key] } : undefined}
                  className={`relative px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide border-r border-neutral-100 last:border-r-0 ${
                    column.align === 'right' ? 'text-right' : 'text-left'
                  } ${column.sortable ? 'cursor-pointer hover:bg-neutral-100' : ''}`}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className={`flex items-center gap-1.5 ${column.align === 'right' ? 'justify-end' : ''}`}>
                    {t(column.label)}
                    {column.sortable && sortColumn === column.key && (
                      sortDirection === 'asc'
                        ? <ArrowUp className="w-3 h-3 text-brand-600" />
                        : <ArrowDown className="w-3 h-3 text-brand-600" />
                    )}
                  </div>
                  <span
                    role="presentation"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      const th = event.currentTarget.parentElement;
                      startResize(column.key, event.clientX, th.getBoundingClientRect().width);
                    }}
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} className="border-b border-neutral-50">
                  {selectable && <td className="px-3 py-2 border-r border-neutral-50" />}
                  {visibleColumns.map((column) => (
                    <td key={column.key} className="px-3 py-2 border-r border-neutral-50 last:border-r-0">
                      <div className="h-3 rounded bg-neutral-100 animate-pulse" style={{ width: `${40 + (column.key.length % 4) * 12}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              sortedData.map((row, index) => {
                const isSelected = (selectedRowId != null && row.id === selectedRowId) || selectedIds.has(row.id);
                return (
                  <tr
                    key={row.id || index}
                    className={`border-b border-neutral-100 h-[34px] hover:bg-neutral-50 cursor-pointer ${
                      isSelected ? 'bg-brand-50' : ''
                    }`}
                    onClick={() => onRowClick(row)}
                  >
                    {selectable && (
                      <td className="px-3 py-1.5 border-r border-neutral-50" onClick={(event) => event.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(row.id)} onChange={(event) => toggleRow(row.id, event)} />
                      </td>
                    )}
                    {visibleColumns.map((column) => (
                      <td
                        key={`${row.id || index}-${column.key}`}
                        className={`px-3 py-1.5 text-sm text-neutral-700 border-r border-neutral-50 last:border-r-0 ${
                          column.align === 'right' ? 'num' : ''
                        }`}
                      >
                        {column.render ? t(column.render(row[column.key], row)) : t(row[column.key])}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
          {totals && !loading && sortedData.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-neutral-200 bg-neutral-50 font-semibold h-[34px]">
                {selectable && <td className="px-3 py-1.5" />}
                {visibleColumns.map((column) => (
                  <td
                    key={`totals-${column.key}`}
                    className={`px-3 py-1.5 text-sm text-neutral-800 ${column.align === 'right' ? 'num' : ''}`}
                  >
                    {t(totals[column.key]) ?? ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {!loading && sortedData.length === 0 && (
        <div className="p-8 text-center">
          <p className="text-neutral-600 text-sm font-medium">{t(emptyTitle)}</p>
          <p className="text-neutral-400 text-xs mt-1">{t(emptyDescription)}</p>
        </div>
      )}
    </div>
  );
};

export default DataTable;
